import { useCallback, useEffect, useState } from 'react';
import {
  disablePushNotifications,
  enablePushNotifications,
  readPushEnrolment,
  type PushEnrolment,
  type PushSetupResult,
} from '../services/pushManager';
import { isIosDevice, isStandalone } from '../lib/platform';
import { hasDeclinedPush, setPushDeclined } from '../lib/pushPreference';

/**
 * Turning alerts on or off, and knowing which they are.
 *
 * This exists because the code it calls did not have a caller. `enablePushNotifications`
 * has been complete and correct since Phase 4 and was never invoked from anywhere, so no
 * device had ever been subscribed (`docs/audits/PUSH-AUDIBILITY-AUDIT.md` §3.1). D39
 * wired enable; `disablePushNotifications` stayed orphaned until the settings toggle,
 * which meant that once alerts were on there was no way back short of "forget all my
 * devices".
 */

/**
 * What alerts are doing on this device, as the settings toggle needs to know it.
 *
 * Four real states, not two, because browser permission is one-way: an app may ask once
 * and can never un-deny. So "off" can only ever mean "this device's subscription is
 * gone" — permission stays granted underneath, which is what lets it be switched back on
 * later without a prompt. `blocked` is the state that traps people: whoever tapped Block
 * on the first-run prompt is unreachable by this app until they change it in the OS.
 *
 * Plus the two platform states the banner already modelled, and `loading` for the beat
 * before the browser has answered — a toggle that flickers through "unsupported" first
 * would be worse than one that waits.
 */
export type AlertsState =
  | 'loading'
  /** Permission never requested: the toggle asks, then subscribes. */
  | 'never-asked'
  /** Permission granted and this device is subscribed. */
  | 'on'
  /** Permission granted, no subscription: switching on re-subscribes silently. */
  | 'off'
  /** Permission denied. The browser will not ask again; only the OS can undo it. */
  | 'blocked'
  /** iOS in a Safari tab: no push until the app is on the Home Screen. */
  | 'install-first'
  | 'unsupported';

export function alertsStateFor(enrolment: PushEnrolment | null): AlertsState {
  if (!enrolment) return 'loading';

  if (!enrolment.supported) {
    // Capability first, platform second. Safari on iOS hides `PushManager` until the
    // app is on the Home Screen, so this is the one place the platform is worth naming
    // — and if Apple ever lifts that, the branch above starts answering instead.
    return isIosDevice() && !isStandalone() ? 'install-first' : 'unsupported';
  }

  if (enrolment.permission === 'denied') return 'blocked';
  if (enrolment.permission === 'granted') return enrolment.subscribed ? 'on' : 'off';

  return 'never-asked';
}

/** What, if anything, the enrolment banner should say. */
export type PushPrompt =
  /** Alerts are on, nothing useful can be said yet, or this device chose off. */
  | 'hidden'
  /** Offer the button. */
  | 'offer'
  /** iOS in a browser tab: no button is possible until the app is installed. */
  | 'install-first'
  /** Permission refused. The browser will not ask again, so a button would be a lie. */
  | 'blocked'
  | 'unsupported';

/**
 * Derived from the same machine as the toggle so the two can never disagree about what
 * state the device is in. `declined` silences only the offer — the informational notes
 * (blocked, unsupported, install-first) are not a prompt and stay.
 */
export function promptFor(enrolment: PushEnrolment | null, declined = false): PushPrompt {
  switch (alertsStateFor(enrolment)) {
    case 'loading':
    case 'on':
      return 'hidden';
    case 'blocked':
      return 'blocked';
    case 'install-first':
      return 'install-first';
    case 'unsupported':
      return 'unsupported';
    default:
      // never-asked, off: permission outlives the subscription it was granted for, so
      // "granted but unsubscribed" still gets the offer — unless this device chose off.
      return declined ? 'hidden' : 'offer';
  }
}

export function usePushEnrolment() {
  const [enrolment, setEnrolment] = useState<PushEnrolment | null>(null);
  const [declined, setDeclined] = useState(hasDeclinedPush);
  const [result, setResult] = useState<PushSetupResult | null>(null);
  const [busy, setBusy] = useState(false);

  /**
   * Re-reads the ACTUAL state from the browser. Nothing here sets `enrolment` from what
   * a call was supposed to have done: a toggle that says on while the subscription
   * failed is worse than one that takes a second to catch up.
   */
  const refresh = useCallback(() => {
    setDeclined(hasDeclinedPush());
    void readPushEnrolment().then(setEnrolment);
  }, []);

  useEffect(refresh, [refresh]);

  /**
   * Must be wired to a tap and nothing else. Browsers reject a permission prompt a page
   * asks for on load, and a technician should be choosing this rather than being
   * ambushed by a system dialog while reading a fault.
   */
  const enable = useCallback(async () => {
    setBusy(true);
    // Switching on is the choice to be asked again, whatever the attempt then returns.
    setPushDeclined(false);
    setResult(await enablePushNotifications());
    setBusy(false);
    refresh();
  }, [refresh]);

  /**
   * Unsubscribes THIS device. Never touches permission — that is not possible — so a
   * later switch back on needs no prompt. Remembers the choice so the banner stops
   * asking.
   */
  const disable = useCallback(async () => {
    setBusy(true);
    setResult(null);
    setPushDeclined(true);
    await disablePushNotifications();
    setBusy(false);
    refresh();
  }, [refresh]);

  return {
    state: alertsStateFor(enrolment),
    prompt: promptFor(enrolment, declined),
    result,
    busy,
    enable,
    disable,
    refresh,
  };
}
